/**
 * Payment contracts, deliberately provider-neutral.
 *
 * The technical task leaves the provider open ("compare fees against your
 * expected mix before committing"), so nothing outside `apps/api/src/payments`
 * knows which one is in use. Swapping means writing one adapter, not touching
 * the ledger, the dashboard or the webhook plumbing.
 */
import { z } from 'zod';
import { SERVICE_PACKS } from './service-packs.js';
export const PAYMENT_PROVIDERS = ['stripe', 'paddle', 'fake'];
/**
 * Checkout accepts service-pack SKUs.
 *
 * Fixed SKUs only — there is deliberately no path that accepts an amount. An
 * arbitrary-amount purchase is the clearest e-money signal there is, so the
 * schema itself has to make one impossible rather than the UI merely not
 * offering it.
 */
export const packIdSchema = z
    .string()
    .refine((sku) => SERVICE_PACKS.some((pack) => pack.sku === sku), 'unknown service pack');
export const createCheckoutSchema = z.object({ packId: packIdSchema });
export const checkoutSessionSchema = z.object({
    /** Where to send the browser. Provider-hosted; we never touch card data. */
    url: z.string(),
    provider: z.enum(PAYMENT_PROVIDERS),
    reference: z.string(),
});
/**
 * Where the buyer's own IP is parked between checkout and the webhook.
 *
 * The webhook arrives from the payment provider's servers, so `request.ip`
 * there is Stripe's, not the customer's. The only moment we ever see the
 * buyer's address is when their browser asks us to create the checkout session
 * — so it is recorded then, against the session id, and read back when the
 * webhook for that same session lands.
 *
 * Redis rather than a column: this exists to make one notification accurate,
 * and an IP kept for seven years beside the tax records is a retention decision
 * nobody asked for.
 */
export const checkoutOriginKey = (reference) => `checkout:origin:${reference}`;
/** Stripe sessions expire after 24 hours; nothing needs this longer. */
export const CHECKOUT_ORIGIN_TTL_SECONDS = 24 * 60 * 60;
export const paymentSummarySchema = z.object({
    id: z.uuid(),
    provider: z.string(),
    /**
     * The provider's own id for the transaction — a Stripe checkout session id.
     * Exposed so the post-checkout page can confirm *this* purchase landed,
     * rather than inferring a purchase from the remaining count moving, which
     * once reported an old welcome grant as a $69 pack.
     */
    providerRef: z.string(),
    amountMinor: z.number().int(),
    currency: z.string(),
    creditsGranted: z.number().int(),
    status: z.string(),
    invoiceUrl: z.string().nullable(),
    createdAt: z.string(),
});
//# sourceMappingURL=payments.js.map